import type { RefObject } from "react";
import { Camera, Loader2 } from "lucide-react";
import { useWorkoutStore } from "@/state/workout-store";

interface VideoStageProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  onStart?: () => void;
}

export function VideoStage({ videoRef, canvasRef, onStart }: VideoStageProps) {
  const status = useWorkoutStore((s) => s.status);

  return (
    <div className="absolute inset-0 bg-black">
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        className="absolute inset-0 h-full w-full object-cover -scale-x-100"
      />
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover -scale-x-100"
      />

      {/* Touch/click screen overlay when camera is not running yet */}
      {status === "idle" && (
        <div
          onClick={onStart}
          className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/60 p-6 transition-colors hover:bg-black/50"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onStart?.();
          }}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <Camera className="size-8 text-white/40 animate-pulse" />
            <p className="max-w-[280px] text-[11px] leading-relaxed text-white/70">
              The camera preview fills your screen. Place your camera at floor level for a side view
              of your body, then{" "}
              <span className="font-semibold text-white">tap anywhere on the screen to start</span>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Small full-screen loader shown while MediaPipe is initializing. */
export function VideoStageLoader() {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/80 backdrop-blur-xs">
      <Loader2 className="size-10 animate-spin text-white/70" />
      <p className="text-sm font-medium text-white/80">Initializing on-device pose detection...</p>
    </div>
  );
}
